import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  ChartDropdownDimensionEnum,
  CubeMeasureEnum,
  FEATURE_INFO_DICTIONARY,
  selectCurrChartDimension,
  selectCurrHiddenColorBuckets,
  setCurrChartDimension,
  setCurrHiddenColorBuckets,
} from '../../../services/data/impl/cube/cubeSlice';
// import useScanData from '../../scanDataApi/hooks/useScanData';
import { isBlueberryFeature } from '../../../services/data/impl/cube/cubeUtils';
import useCurrServicesAndMeasures from '../../customerSummary/hooks/useCurrServicesAndMeasures';
import {
  ValidPointsDropdownOptions,
  selectShowValidPointsDropdown,
  setShowValidPointsDropdown,
} from '../../scanDataApi/scanDataSlice';
import {
  RanchViewFeaturesEnum,
  ResetItemsEnum,
  resetSelectFields,
  selectCurrFeature,
  selectCurrMeasure,
  selectCurrRanchViewFeature,
  selectCurrScanID,
  setCurrFeature,
  setCurrMeasure,
} from '../../select/selectSlice';
import { RanchPageViewEnum, selectRanchPageView } from '../ranchesSlice';

export type RanchControllerContextType = {
  currMeasure: CubeMeasureEnum;
  currChartDimension: ChartDropdownDimensionEnum;
  showValidPointsDropdown: ValidPointsDropdownOptions;
  changeMeasure: (newMeasure: CubeMeasureEnum) => void;
} | null;

export const RanchControllerContext =
  createContext<RanchControllerContextType>(null);

export const RanchControllerProvider: React.FC<Props> = React.memo(
  ({ children }) => {
    const dispatch = useAppDispatch();
    const currFeature = useAppSelector(selectCurrFeature);
    const currMeasure = useAppSelector(selectCurrMeasure);
    const currChartDimension = useAppSelector(selectCurrChartDimension);
    const showValidPointsDropdown = useAppSelector(
      selectShowValidPointsDropdown
    );
    const ranchPageView = useAppSelector(selectRanchPageView);
    const currRanchViewFeature = useAppSelector(selectCurrRanchViewFeature);
    const currScanId = useAppSelector(selectCurrScanID);
    const currHiddenColorBuckets = useAppSelector(selectCurrHiddenColorBuckets);
    const {
      servicesAndMeasuresWithFeatureFilter,
      servicesAndMeasuresWithoutFeatureFilter,
    } = useCurrServicesAndMeasures();

    // automatically switch to available service if currently selected feature is not available.  If on ranch page table vew, reset the feature to all features and hide the feature and measure dropdowns (hide logic handled in selectHeader.tsx).
    useEffect(() => {
      // Reset feature to all if on ranch page table view bc we no longer filter by feature since we show a summary table of features now.
      // Measure doesn't need to be reset bc the table doesn't filter by measure.
      if (
        ranchPageView === RanchPageViewEnum.ranchSummary &&
        currRanchViewFeature === RanchViewFeaturesEnum.Tables
      ) {
        dispatch(resetSelectFields([ResetItemsEnum.Feature]));
        return;
      }

      if (
        servicesAndMeasuresWithoutFeatureFilter.availableServices.length > 0 ||
        servicesAndMeasuresWithoutFeatureFilter.availableServices.includes(
          currFeature
        )
      )
        return;

      dispatch(
        setCurrFeature(
          servicesAndMeasuresWithoutFeatureFilter.availableServices[0]
        )
      );
    }, [
      servicesAndMeasuresWithoutFeatureFilter.availableServices,
      ranchPageView,
      currRanchViewFeature,
    ]);

    // Set the chart dimension based on the feature
    useEffect(() => {
      if (!currFeature) return;

      // if Blueberry case - set perRowside chart as initial view
      if (isBlueberryFeature(currFeature)) {
        dispatch(setCurrChartDimension(ChartDropdownDimensionEnum.perRowside));
        return;
      }

      // if current dimension exists in feature config - keep it selected on the chart
      if (
        FEATURE_INFO_DICTIONARY[currFeature]
          .map((el) => el.dimension)
          .includes(currChartDimension)
      ) {
        return;
      }

      // if not - set first of available dimensions
      dispatch(
        setCurrChartDimension(FEATURE_INFO_DICTIONARY[currFeature][0].dimension)
      );
    }, [currFeature]);

    // set first available measure if current measure not available (attempt to set count as default if it's available first).  On ranch summary, don't pick color measure.
    useEffect(() => {
      // Update measure if currMeasure not available or if on ranch summary and currMeasure is color.
      if (
        !servicesAndMeasuresWithFeatureFilter.availableMeasures.includes(
          currMeasure
        ) ||
        (ranchPageView === RanchPageViewEnum.ranchSummary &&
          currMeasure === CubeMeasureEnum.DetectionFactHasColorHSV)
      ) {
        // If on ranch summary table, don't auto change measure so that "All Measures" can be selected as a filter
        if (
          ranchPageView === RanchPageViewEnum.ranchSummary &&
          currRanchViewFeature === RanchViewFeaturesEnum.Tables &&
          currMeasure === CubeMeasureEnum.none
        )
          return;

        if (servicesAndMeasuresWithFeatureFilter.availableMeasures.length > 0) {
          // if on ranch summary, make sure note to pick color
          let availableMeasuresArrFiltered = [
            ...servicesAndMeasuresWithFeatureFilter.availableMeasures,
          ];
          if (ranchPageView === RanchPageViewEnum.ranchSummary) {
            availableMeasuresArrFiltered =
              servicesAndMeasuresWithFeatureFilter.availableMeasures.filter(
                (measure) =>
                  measure !== CubeMeasureEnum.DetectionFactHasColorHSV
              );
          }

          if (availableMeasuresArrFiltered.length) {
            // If count is available, set it to that as a default, otherwise set to first available measure
            if (
              availableMeasuresArrFiltered.includes(
                CubeMeasureEnum.detectionFactAvgCountPerMeter
              )
            ) {
              changeMeasure(CubeMeasureEnum.detectionFactAvgCountPerMeter);
            } else {
              changeMeasure(availableMeasuresArrFiltered[0]);
            }
          }
        }
      }
    }, [
      servicesAndMeasuresWithFeatureFilter.availableMeasures,
      currMeasure,
      ranchPageView,
      currRanchViewFeature,
    ]);

    const changeMeasure = useCallback(
      (newMeasure: CubeMeasureEnum) => {
        // Space for more complex updates in many measures in future
        const measureUpdateStrategies: { [key: string]: () => void } = {
          [CubeMeasureEnum.detectionFactAvgCountPerMeter]: () => {
            dispatch(
              setCurrChartDimension(ChartDropdownDimensionEnum.distribution)
            );
            dispatch(
              setShowValidPointsDropdown(
                ValidPointsDropdownOptions.showValidPoints
              )
            );
          },
          [CubeMeasureEnum.DetectionFactHasColorHSV]: () => {
            dispatch(
              setCurrChartDimension(
                ChartDropdownDimensionEnum.colorDistribution
              )
            );
            dispatch(
              setShowValidPointsDropdown(
                ValidPointsDropdownOptions.showValidPoints
              )
            );
          },
          [CubeMeasureEnum.DetectionFactAvgLength]: () => {
            dispatch(
              setCurrChartDimension(ChartDropdownDimensionEnum.distribution)
            );
            dispatch(
              setShowValidPointsDropdown(
                ValidPointsDropdownOptions.showValidPoints
              )
            );
          },
        };

        dispatch(setCurrMeasure(newMeasure));

        if (newMeasure in measureUpdateStrategies) {
          measureUpdateStrategies[newMeasure]();
        }
      },
      [dispatch]
    );

    // Clear hidden buckets when changing scan
    useEffect(() => {
      if (currChartDimension !== ChartDropdownDimensionEnum.colorDistribution)
        return;
      if (!currHiddenColorBuckets.length) return;

      dispatch(setCurrHiddenColorBuckets([]));
    }, [currScanId]);

    const value = {
      currMeasure,
      currChartDimension,
      showValidPointsDropdown,

      changeMeasure,
    };

    return (
      <RanchControllerContext.Provider value={value}>
        {children}
      </RanchControllerContext.Provider>
    );
  }
);

type Props = { children: React.ReactNode };

export const useRanchControllerContext = () => {
  const context = useContext(RanchControllerContext);
  if (!context) {
    throw new Error(
      'useRanchControllerContext must be used within a RanchControllerContext.'
    );
  }
  return context;
};
